<script lang="ts" module>
  import type { AiSuggestion } from '@/teoCore/TeoApi';
  import LoadingArea from '@/ui/base/LoadingArea.svelte';
  import CTAButton from '@/ui/elements/buttons/CTAButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useActiveText } from '@/utils/teo-context';

  import { generateDiff } from '../helpers/generate-diff';
  import LoadingButton from '../helpers/LoadingButton.svelte';
  import AISuggestionsContent from './AISuggestionsContent.svelte';

  namespace AISuggestionsPanel {
    export interface Props {
      suggestions: AiSuggestion[];
      finalStateMessage?: string;
      loading?: boolean;
      onSuggestionUsed?: (aiSuggestion: AiSuggestion) => void;
      onRemoveSuggestion: (index: number) => void;
      onLoadAiSuggestionClick: (type: string) => void;
      promptType?: 'buttons' | 'dropdown';
      promptItems?: DropDownItem[];
      promptItemSelected?: string;
      onPromptSelect?: (item: string) => void;
    }
  }
</script>

<script lang="ts">
  let teoText = useActiveText();
  let props: AISuggestionsPanel.Props = $props();

  // Calculates the index of the shown suggsestion
  let selectedIndex = $state(0);
  $effect(() => {
    selectedIndex = Math.max((props.suggestions?.length ?? 0) - 1, 0);
  });

  function onInsertClick(): void {
    const suggestion = props.suggestions[selectedIndex];
    if (!suggestion) {
      console.warn('No AI suggestion provided');
      return;
    }
    teoText.current.ai.useSuggestion({
      markerId: suggestion.context.replacementWrapper,
      replacement: suggestion.text,
    });
    props.onSuggestionUsed?.(suggestion);
  }

  function onSelectSuggestion(index: number): void {
    selectedIndex = index;
  }

  let currentSelectedSuggestion = $derived(props.suggestions[selectedIndex]);

  let text = $derived.by(() => {
    if (!currentSelectedSuggestion) {
      return props.finalStateMessage ?? '';
    }

    return generateDiff(
      currentSelectedSuggestion?.context.text ?? '',
      currentSelectedSuggestion?.text ?? '',
    );
  });

  let hidden = $state(false);
  $effect(() => {
    hidden = currentSelectedSuggestion?.hidden ?? false;
  });

  function handleUnhideSuggestion(): void {
    if (currentSelectedSuggestion) {
      currentSelectedSuggestion.hidden = false;
      hidden = false;
    }
  }

  let selectedPrompt = $derived(props.promptItemSelected ?? props.promptItems?.[0]?.value ?? '');

  // true if any of the branches below the divider are shown
  let showsPanelBody = $derived(
    props.promptType !== 'buttons' ||
      props.suggestions?.length ||
      props.loading ||
      props.promptItems,
  );
</script>

<!-- <AIContentHeader pages={suggestions?.length ?? 0} bind:current={currentPage}></AIContentHeader> -->
{#if showsPanelBody}
  <hr class="border-t-c-divider" />
{/if}
{#if props.loading}
  <div class="flex h-20 overflow-hidden rounded-sm">
    <LoadingArea />
  </div>
{:else}
  <div class={`flex ${props.promptItems ? 'justify-between' : 'justify-end'}`}>
    {#if props.promptItems}
      <DropDown
        items={props.promptItems}
        disabled={props.promptItems.length <= 1}
        selected={selectedPrompt}
        onItemClick={props.onPromptSelect}
      ></DropDown>
    {/if}
    {#if !props.suggestions?.length && !props.promptItems && props.promptType !== 'buttons'}
      <div class="flex justify-end">
        <LoadingButton
          icon="MagicButton"
          onclick={() => props.onLoadAiSuggestionClick(selectedPrompt)}>KI-Vorschlag</LoadingButton
        >
      </div>
    {:else if props.suggestions?.length}
      <div class="flex justify-end">
        <LoadingButton
          tooltip="KI-Vorschlag laden"
          icon="MagicExchange"
          onclick={() => props.onLoadAiSuggestionClick(selectedPrompt)}
        ></LoadingButton>
      </div>
    {/if}
  </div>
  {#if props.suggestions?.length}
    <AISuggestionsContent
      count={props.suggestions?.length ?? 0}
      loading={false}
      onRemoveSuggestion={props.onRemoveSuggestion}
      {onSelectSuggestion}
      selected={selectedIndex}
      tpi={currentSelectedSuggestion?.teo_result.tpi.percentage}
      {text}
      {hidden}
      onUnhideSuggestion={handleUnhideSuggestion}
    />
    {#if props.suggestions.length > 0}
      <div class="flex justify-end">
        <CTAButton
          tooltip="Übernehmen"
          onclick={onInsertClick}
          variant="primary"
          disabled={hidden}
          iconLeft="Transfer">Übernehmen</CTAButton
        >
      </div>
    {/if}
  {/if}
{/if}
